import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { RocketLaunch, FlashOn, Handshake, Description } from '@mui/icons-material';

interface QuickAssemblyProps {
  open: boolean;
  onClose: () => void;
  selectedObject: any;
  currentPage: number;
}

export function QuickAssembly({ open, onClose, selectedObject, currentPage }: QuickAssemblyProps) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.background.paper,
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Top Border */}
      <Box
        sx={{
          height: 4,
          background: 'linear-gradient(90deg, #ff9800, #ffc107)',
        }}
      />

      <DialogContent sx={{ pt: 3, pb: 2, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          {/* Rocket Icon */}
          <RocketLaunch
            sx={{
              fontSize: 64,
              color: theme.palette.primary.main,
              transform: 'rotate(45deg)',
            }}
          />

          {/* Main Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#ff9800',
              textAlign: 'center',
            }}
          >
            Quick Assembly
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 500,
            }}
          >
            Coming Soon
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
              lineHeight: 1.6,
              maxWidth: 400,
            }}
          >
            We're building a lightning-fast assembly creator for those oddball widgets and
            thingamabobs you need on the fly. Need a custom wire stretcher? No problem.
          </Typography>

          {/* Feature Tags */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: '#4caf50',
                  color: 'white',
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: theme.shadows[2],
                }}
              >
                <FlashOn sx={{ fontSize: 16 }} />
                Lightning Fast
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: alpha(theme.palette.grey[700], 0.8),
                  color: 'white',
                  borderRadius: 2,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  boxShadow: theme.shadows[2],
                }}
              >
                <Description sx={{ fontSize: 16, color: '#2196f3' }} />
                On-the-Fly Creation
              </Box>
            </Box>
          </Box>

          {/* Bottom Message */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontStyle: 'italic',
              textAlign: 'center',
              mt: 2,
            }}
          >
            Currently in development - We're wrapping up our core assembly system first to ensure
            the best foundation for quick assembly creation.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            px: 3,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.9),
            },
          }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
}
